import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  Fragment,
  useCallback,
  useMemo,
} from 'react';
import GoogleMapReact from 'google-map-react';
import { useLazyQuery } from 'react-apollo';
import ReactSVG from 'react-svg';
import mapResearch from 'map-research.svg';
import useWindowSize from '../../hooks/useWindowSize';
import { MLSMapContext } from '../../context/MLSMapContext';
import { MLS_LISTING_MARKERS, MLS_MAP_LISTINGS } from '../../constants/queries';
import MLSFilterBar from './MLSFilterBar';
import MLSFiltersDropdown from './MLSFiltersDropdown';
import PaginationResults from '../Map/PaginationResults';
import MapPagination from '../Map/MapPagination';
import LoadingCard from '../Loading/LoadingCard';
import RealEstateCard from '../RealEstate/RealEstateCard';
import NoResults from '../NoResults';
import MLSMapMarker, { MemoizedMLSMapMarker } from './MLSMapMarker';
import MapControl from '../Map/MapControl';
import LoadingDots from '../Loading/LoadingDots';
import MLSTypeDropdown from './MLSTypeDropdown';
import DropdownPortal from '../DropdownPortal';
import MLSCategoryDropdown from './MLSCategoryDropdown';
import MLSSortDropdown from './MLSSortDropdown';
import WindowQuery from '../../utils/WindowQuery';
import mls_amenities_filters from '../../constants/mls_amenities_filters.json';
import MLSStatusDropdown from './MLSStatusDropdown';
import MLSShowModal from './MLSShowModal';

export default function MLSMapPage({ areas }) {
  const cardsWrapperRef = useRef();

  const updateQuery = new WindowQuery();

  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const {
    meta,
    setMeta,
    mapCenter,
    setMapCenter,
    mapBounds,
    setMapBounds,
    showFilters,
    bedrooms,
    bathrooms,
    minPrice,
    maxPrice,
    minSqFt,
    maxSqFt,
    typeFilter,
    categoryFilter,
    statusFilters,
    mapVisible,
    setMapVisible,
    searchOnMove,
    setSearchOnMove,
    setShowSearchOnMove,
    showRedoSearch,
    setShowRedoSearch,
    typeDropdownVisible,
    categoryDropdownVisible,
    statusDropdownVisible,
    sortDropdownVisible,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    filterStatuses,
    amenities,
    mapAreas,
    setMapLoaded,
    sortBy,
    mapRef,
    zipcode,
    setZipcode,
    setHighlightedMarkerID,
    listingURL,
    setListingURL,
    currentListing,
    setCurrentListing,
    addressContains,
  } = useContext(MLSMapContext);

  const [page, setPage] = useState(1);
  const [localBounds, setLocalBounds] = useState(null);

  const [fetchListings, { data, loading }] = useLazyQuery(MLS_MAP_LISTINGS, {
    variables: {
      page: String(page),
      in_polygon: mapBounds,
    },
  });
  const [
    fetchListingMarkers,
    { data: markerData, loading: markersLoading },
  ] = useLazyQuery(MLS_LISTING_MARKERS, {
    variables: {
      page: String(page),
      in_polygon: mapBounds,
    },
    fetchPolicy: 'cache-first',
  });

  const memoizedListingMarkers = useMemo(() => markerData, [markerData]);

  const buildVariables = useCallback(() => {
    const newVariables = {
      page: '1',
      in_polygon: mapBounds,
    };

    if (typeFilter) newVariables.property_type_eq = typeFilter.value;
    if (addressContains) newVariables.address_cont = addressContains;
    if (
      categoryFilter.length &&
      (typeFilter.value !== 'E' || typeFilter.value !== 'F')
      // Commercial - For Sale (E) & Commercial - For Lease (F) don't have sub type filters
    )
      newVariables.sub_type_eq_any = categoryFilter.map(c => c.value);
    if (statusFilters.length) {
      const listingStatus = statusFilters
        .flatMap(s => s.value)
        .filter(v => v != null && String(v).trim() !== '');
      if (listingStatus.length)
        newVariables.listing_status_eq_any = listingStatus;
    }
    if (mapAreas.length > 0)
      newVariables.area_name_eq_any = mapAreas.map(area => area.name);
    if (zipcode) newVariables.postal_code_cont = zipcode;
    if (bedrooms) newVariables.total_bedrooms_gteq = parseInt(bedrooms);
    if (bathrooms) newVariables.total_baths_gteq = parseInt(bathrooms);
    if (minPrice)
      newVariables.list_price_gteq = parseInt(minPrice.replace(/,/g, ''));
    if (maxPrice)
      newVariables.list_price_lteq = parseInt(maxPrice.replace(/,/g, ''));
    if (minSqFt)
      newVariables.apx_sqft_gteq = parseInt(minSqFt.replace(/,/g, ''));
    if (maxSqFt)
      newVariables.apx_sqft_lteq = parseInt(maxSqFt.replace(/,/g, ''));
    if (minYearBuilt)
      newVariables.year_built_gteq = parseInt(minYearBuilt.replace(/,/g, ''));
    if (maxYearBuilt)
      newVariables.year_built_lteq = parseInt(maxYearBuilt.replace(/,/g, ''));
    if (minLotSize)
      newVariables.acreage_gteq = parseInt(minLotSize.replace(/,/g, ''));
    if (maxLotSize)
      newVariables.acreage_lteq = parseInt(maxLotSize.replace(/,/g, ''));
    if (maxLotSize)
      newVariables.acreage_lteq = parseInt(maxLotSize.replace(/,/g, ''));
    if (filterStatuses) {
      filterStatuses.forEach(status => {
        switch (status.label) {
          case 'Newly Listed':
            newVariables.days_on_market_lteq = 7;
            break;

          case 'In Forclosure':
            newVariables.sale_type = ['In Foreclosure'];
            break;

          case 'Bank-Owned':
            newVariables.sale_type = ['Bankruptcy Property'];
            break;

          case 'Virtual Tours':
            newVariables.unbranded_virtual_tour_present = 1;
            break;

          case 'HOA':
            newVariables.fees_include = ['fee_a', 'fee_b'];
            break;

          case 'Price Reduced':
            newVariables.price_reduced = true;
            break;

          default:
            break;
        }
      });
    }
    if (amenities) {
      amenities.forEach(amenity => {
        switch (amenity.value) {
          case 'waterfront':
            newVariables.waterfront = mls_amenities_filters[amenity.value];
            break;

          case 'garage':
            newVariables.park_features = mls_amenities_filters[amenity.value];
            break;

          case 'pool':
            newVariables.exterior = mls_amenities_filters[amenity.value];
            break;

          case 'tennis-court':
            newVariables.exterior = mls_amenities_filters[amenity.value];
            break;

          case 'waterview':
            newVariables.waterview = mls_amenities_filters[amenity.value];
            break;

          case 'golf-course':
            newVariables.project_facilities =
              mls_amenities_filters[amenity.value];
            break;

          case 'single-story-only':
            newVariables.num_stories_lteq = 1;
            break;

          default:
            break;
        }
      });
    }
    if (sortBy && sortBy.value) {
      switch (sortBy.value) {
        case 'price_high_low':
          newVariables.price_sort = 'high';
          break;

        case 'price_low_high':
          newVariables.price_sort = 'low';
          break;

        case 'newest':
          newVariables.age_sort = 'new';
          break;

        default:
          break;
      }
    }

    return newVariables;
  }, [
    addressContains,
    amenities,
    bathrooms,
    bedrooms,
    categoryFilter,
    filterStatuses,
    mapAreas,
    mapBounds,
    maxLotSize,
    maxPrice,
    maxSqFt,
    maxYearBuilt,
    minLotSize,
    minPrice,
    minSqFt,
    minYearBuilt,
    sortBy,
    statusFilters,
    typeFilter,
    zipcode,
  ]);
  useEffect(() => {
    const onBackButtonClick = () => {
      // If the user is on the property show modal and clicks the browser's back button, then close the modal.
      if (listingURL) {
        if (window.location.pathname.split('/').length === 3) {
          window.location.replace('/for-sale');
        }
        setListingURL(null);
        setCurrentListing(null);
      } else {
        // Else if the user is on the map search page and clicks the browser's back button, navigate them to
        // the most recent DF page they had visited.
        const prevURL = window.localStorage.getItem('previousURL');
        document.location.href = prevURL;
      }
    };
    window.addEventListener('popstate', onBackButtonClick);

    return () => window.removeEventListener('popstate', onBackButtonClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingURL]);

  const scrollToTop = () => {
    if (cardsWrapperRef.current && cardsWrapperRef.current.scrollTo)
      cardsWrapperRef.current.scrollTo(0, 0);
  };

  useEffect(() => {
    if (data && data.mls_listings) {
      setMeta({
        total_count: data.mls_listings.total_count,
        current_page: data.mls_listings.current_page,
        per_page: data.mls_listings.per_page,
        total_pages: data.mls_listings.total_pages,
      });
    }
  }, [data, setMeta]);

  // Handle Page change refetch
  useEffect(() => {
    fetchListings({ variables: { ...buildVariables(), page: String(page) } });
    fetchListingMarkers({ variables: { ...buildVariables() } });
    scrollToTop();
  }, [buildVariables, fetchListingMarkers, fetchListings, page]);

  // Refetch listings and reset page
  useEffect(() => {
    const variables = buildVariables();

    fetchListings({ variables });
    fetchListingMarkers({ variables });
    setPage(1);
    scrollToTop();
  }, [
    bedrooms,
    bathrooms,
    minPrice,
    maxPrice,
    minSqFt,
    maxSqFt,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    filterStatuses,
    amenities,
    sortBy,
    mapBounds,
    categoryFilter,
    typeFilter,
    buildVariables,
    fetchListings,
    fetchListingMarkers,
  ]);

  const updateBounds = ({
    bounds: newBounds,
    center: newCenter,
    // zoom: newZoom,
  }) => {
    setLocalBounds([
      newBounds.sw.lng,
      newBounds.sw.lat,
      newBounds.ne.lng,
      newBounds.ne.lat,
    ]);
    setMapCenter(newCenter);
    if (searchOnMove) {
      setMapBounds([
        newBounds.sw.lng,
        newBounds.sw.lat,
        newBounds.ne.lng,
        newBounds.ne.lat,
      ]);
    } else {
      setShowSearchOnMove(true);
      setShowRedoSearch(true);
    }
  };

  const searchTitle = useMemo(() => {
    const titleType =
      typeFilter.label === 'Rental' ||
      typeFilter.label === 'Commercial for Lease'
        ? 'For Rent'
        : 'For Sale';
    if (mapAreas?.length) {
      const [firstArea] = mapAreas;
      if (firstArea?.name?.includes('30A'))
        return `30A Properties ${titleType}`;
      if (firstArea?.name?.includes('Miramar'))
        return `Miramar Beach Properties ${titleType}`;
      return `${firstArea?.name} Properties ${titleType}`;
    }
    return `Destin Homes ${titleType}`;
  }, [mapAreas, typeFilter.label]);

  useEffect(() => {
    if (searchTitle && !listingURL) {
      document.title = `${searchTitle} | Destin Florida`;
    } else if (listingURL && currentListing) {
      document.title = `${currentListing?.address} | Destin Florida`;
    }
  }, [mapAreas, searchTitle, listingURL, currentListing]);

  useEffect(() => {
    if (window.dataLayer && data && !listingURL) {
      window.dataLayer.push({
        content_ids: [
          ...data.mls_listings.collection
            .slice(0, 10)
            .map(listing => listing.id),
        ],
      });
    }
  }, [data, listingURL]);

  return (
    <div className="map-container">
      <div className="filter-section real-estate" id="filter-section">
        <MLSFilterBar />
      </div>
      {isMobile && (
        <DropdownPortal
          visible={
            typeDropdownVisible ||
            statusDropdownVisible ||
            categoryDropdownVisible ||
            sortDropdownVisible
          }
        >
          {typeDropdownVisible && <MLSTypeDropdown />}
          {statusDropdownVisible && <MLSStatusDropdown />}
          {categoryDropdownVisible && <MLSCategoryDropdown />}
          {sortDropdownVisible && <MLSSortDropdown />}
        </DropdownPortal>
      )}

      <section
        className="cards-wrapper"
        style={{
          display: isMobile && mapVisible && !showFilters ? 'none' : 'block',
        }}
        ref={cardsWrapperRef}
      >
        <MLSFiltersDropdown areas={areas} />

        <h1 className="search-results-title">{searchTitle}</h1>

        {(!isMobile || !mapVisible) && !showFilters && (
          <div className="card-section">
            {!loading ||
              (meta && meta.total_count > 0 && isMobile && (
                <PaginationResults
                  totalCount={parseInt(meta.total_count)}
                  firstIndex={
                    parseInt(meta.per_page) * parseInt(meta.current_page) -
                    parseInt(meta.per_page) +
                    1
                  }
                  lastIndex={Math.min(
                    parseInt(meta.per_page) * parseInt(meta.current_page),
                    parseInt(meta.total_count)
                  )}
                  queryType="listings"
                />
              ))}

            {loading || !data
              ? [...Array(10)].map((_, ind) => <LoadingCard key={ind} />)
              : data.mls_listings.collection.map((mlsListing, ind) => (
                  <RealEstateCard
                    key={ind}
                    data={mlsListing}
                    onMouseOver={() => {
                      setHighlightedMarkerID(mlsListing.id);
                    }}
                    onMouseLeave={() => {
                      setHighlightedMarkerID(null);
                    }}
                    onFocus={() => {
                      setHighlightedMarkerID(mlsListing);
                    }}
                    onBlur={() => {
                      setHighlightedMarkerID(null);
                    }}
                    onClick={() => {
                      setListingURL(
                        window.Routes.mls_address_show_path(
                          mlsListing.address_show_url
                        )
                      );
                      updateQuery.push(
                        window.location.href,
                        `for-sale/${mlsListing.address_show_url}`
                      );
                    }}
                  />
                ))}

            {data &&
              data.mls_listings &&
              (!data.mls_listings.collection.length ? (
                <NoResults />
              ) : (
                <MapPagination
                  totalCount={data.mls_listings.total_count}
                  perPage={data.mls_listings.per_page}
                  totalPages={data.mls_listings.total_pages}
                  currentPage={data.mls_listings.current_page}
                  queryType="listings"
                  onPageChange={({ selected }) => {
                    setPage(
                      Math.min(selected + 1, data.mls_listings.total_pages)
                    );
                  }}
                />
              ))}
          </div>
        )}
      </section>

      {(!isMobile || (isMobile && mapVisible && !showFilters)) && (
        <div className="map-section">
          {markersLoading ? (
            <MapControl position="top center">
              <LoadingDots />
            </MapControl>
          ) : (
            <MapControl
              position="top center"
              onClick={() => {
                if (showRedoSearch) {
                  setMapBounds(localBounds);
                  setShowRedoSearch(false);
                } else {
                  setSearchOnMove(!searchOnMove);
                }
              }}
            >
              {showRedoSearch ? (
                <Fragment>
                  <ReactSVG src={mapResearch} className="map-research-icon" />
                  <span>Redo Search</span>
                </Fragment>
              ) : (
                <Fragment>
                  <input
                    aria-label="Search as I move"
                    type="checkbox"
                    checked={searchOnMove}
                  />
                  <span>Search as I move</span>
                </Fragment>
              )}
            </MapControl>
          )}

          {zipcode && !isMobile && (
            <MapControl
              position="bottom left"
              onClick={() => {
                updateQuery.remove('zipcode');
                setZipcode('');
              }}
              name="Remove Zipcode Filter"
              aria-label="Remove Zipcode Filter"
              title="Remove Zipcode Filter"
            >
              <span>Remove Zipcode Filter</span>
            </MapControl>
          )}

          <GoogleMapReact
            bootstrapURLKeys={{
              // @TODO: Add this to an environment variable or something
              key: 'AIzaSyATvnQePj9OTTCZJ6VkugYM8dRGFPJ5HmQ',
            }}
            ref={mapRef}
            center={mapCenter}
            onChange={updateBounds}
            defaultZoom={11}
            yesIWantToUseGoogleMapApiInternals
            options={map => ({
              clickableIcons: false,
              zoomControlOptions: { position: map.ControlPosition.TOP_LEFT },
            })}
            onGoogleApiLoaded={() => {
              setMapLoaded(true);
              return null;
            }}
          >
            {markerData &&
              markerData.mls_listings &&
              memoizedListingMarkers.mls_listings.collection.map(mlsListing => {
                if (
                  data &&
                  data.mls_listings &&
                  data.mls_listings.collection.some(l => l.id === mlsListing.id)
                ) {
                  return (
                    <MLSMapMarker
                      key={mlsListing.listing_id}
                      lat={mlsListing.latitude}
                      lng={mlsListing.longitude}
                      data={mlsListing}
                    />
                  );
                }
                return (
                  <MemoizedMLSMapMarker
                    key={mlsListing.listing_id}
                    lat={mlsListing.latitude}
                    lng={mlsListing.longitude}
                    data={mlsListing}
                  />
                );
              })}
          </GoogleMapReact>
        </div>
      )}

      {isMobile && (
        <button
          name={`View as ${mapVisible ? 'List' : 'Map'}`}
          type="button"
          className="mobile-map-toggle"
          onClick={() => setMapVisible(!mapVisible)}
        >
          View as {mapVisible ? 'List' : 'Map'}
        </button>
      )}

      {listingURL && (
        <MLSShowModal
          listingURL={listingURL}
          areas={areas}
          isModal
          closeModal={() => {
            const prevURL = window.localStorage.getItem('prevUrl');
            window.history.pushState(null, null, prevURL ?? '/for-sale');
            setListingURL(null);
            setCurrentListing(null);
          }}
        />
      )}
    </div>
  );
}
